import { Injectable } from '@angular/core';
import type { Workbook } from 'exceljs';

/**
 * Turns an .xlsx file into the first worksheet's rows.
 *
 * The contract every implementation must honour: rows are POSITION-PRESERVING. A blank
 * cell becomes '' and keeps its index, because the import applies values to the grid
 * purely by column index — a shifted row writes plan data into the wrong column.
 * Padding rows wider than their content is safe; narrower is not.
 *
 * Sheet row 1 is array index 0 (the metadata row); index 1 is the column headers.
 *
 * Deliberately uses counted index loops, NOT sheet.eachRow / row.eachCell: both skip
 * empty rows and cells by default, which is exactly the column shift this must prevent.
 *
 * cell.text gives the cell's display string and '' for blanks. Note it does NOT apply
 * the cell's Excel number format, unlike SheetJS's raw:false which used the cached
 * formatted text: 12.5 formatted "0.00" reads back as "12.5", not "12.50", and a native
 * Date cell reads back as a full JS Date string. Our own exports set no numberFormat, so
 * exported values are unaffected; hand-edited cells carrying an explicit format are the
 * exposed case. Values are handed to ag-grid's valueSetter, which parses numerics, so
 * this is a display-string difference rather than a numeric one.
 *
 * Non-master merged cells are normalised to '' to match the previous SheetJS reader.
 * ExcelJS otherwise repeats the master's value across the merged range, and because
 * validateStructureMatch only checks header-cell and row COUNTS — never data-row cell
 * values — such a file would pass validation and write duplicated values into columns
 * that should stay untouched.
 */
@Injectable({providedIn: 'root'})
export class ExcelWorkbookReader {
  async readFirstSheet(file: File): Promise<string[][]> {
    // Loaded dynamically to keep ExcelJS's ~947 KB out of the eager initial bundle: this
    // app has zero lazy routes, so a top-level import would ship it to every user, not
    // just the ones who use the import feature.
    //
    // ExcelJS is a CommonJS package. When the dynamic import() lands in its own bundler
    // chunk (as it does here, precisely because we want it split out), the chunk re-exports
    // the CJS module only as `default` — there are no synthesized named exports the way a
    // fully-inlined static import would get. Unwrap `default` explicitly rather than
    // destructuring `Workbook` straight off the namespace object.
    const importedExcelJS = await import('exceljs');
    const ExcelJS = importedExcelJS.default ?? importedExcelJS;
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());

    const sheet = workbook.worksheets[0];
    if (!sheet) {
      return [];
    }

    const width = sheet.columnCount;
    const rows: string[][] = [];
    for (let r = 1; r <= sheet.rowCount; r++) {
      const row = sheet.getRow(r);
      const cells: string[] = [];
      for (let c = 1; c <= width; c++) {
        const cell = row.getCell(c);
        // A non-master merged cell reports the master's value in ExcelJS, where SheetJS
        // wrote ''. Emitting '' preserves that behaviour and keeps applyImportedDataToGrid's
        // `cellValue === '' -> continue` guard skipping the cell, so a merge cannot write a
        // duplicated value into a column that should stay untouched.
        cells.push(cell.isMerged && cell.master !== cell ? '' : (cell.text ?? ''));
      }
      rows.push(cells);
    }
    return rows;
  }
}
